import type { IconName } from '@launchpad-ui/icons';

import type { TooltipSeriesItem } from '../../types';
import type { ChartDataPoint } from '../../types/ChartDataPoint';
import type { SeriesDisplayMetadata } from '../types/ChartContext';
import type { XDataKey } from '../types/XDataKey';

/**
 * Gets all series values at a specific X value from the chart data.
 *
 * Finds the data point with the matching X value and extracts each series' Y value
 * along with display metadata. Only includes series that have non-null data at the X value.
 *
 * This is useful when series have data at different X values (e.g., different
 * sampling times) and you want to show only the series that have measurements
 * at a particular point.
 *
 * @param xValue - The X value to look up (in data space, e.g., timestamp or category string)
 * @param chartData - Array of data points from the chart
 * @param seriesDisplayMetadata - Display metadata from ChartContext (names, colors, dataKeys)
 * @param xDataKey - The key to access X values in data objects
 * @param defaultRowIcon - Default icon type from tooltip-level rowIcon prop (optional)
 * @returns Array of series data objects, filtered to only those with data at the X value
 *
 * @example
 * ```typescript
 * const seriesAtPoint = getSeriesValuesAtX(
 *   1234567890, // timestamp or 'Control' for categories
 *   chartData,
 *   seriesDisplayMetadata,
 *   'timestamp'
 * );
 * // Returns: [{ key: 'series-cpu', name: 'CPU', value: 45, color: '#blue', ... }, ...]
 * // Only includes series that have data at that timestamp
 * ```
 */
export function getSeriesValuesAtX(
  xValue: number | string,
  chartData: ChartDataPoint[],
  seriesDisplayMetadata: Map<string, SeriesDisplayMetadata>,
  xDataKey: XDataKey,
  defaultRowIcon?: IconName,
): TooltipSeriesItem[] {
  const seriesItems: TooltipSeriesItem[] = [];

  // Find the data point at this X value
  const datum = chartData.find((d) => d[xDataKey] === xValue);

  if (!datum) {
    return seriesItems;
  }

  // Extract values for each series from this data point
  for (const [seriesKey, displayMeta] of seriesDisplayMetadata.entries()) {
    const yValue = datum[displayMeta.dataKey];

    // Only include series that have non-null data at this point
    if (typeof yValue === 'number') {
      // Resolve icon type: series tooltipRowIcon takes precedence, then tooltip-level rowIcon, default to 'diamond'
      const rowIcon: IconName = displayMeta.tooltipRowIcon ?? defaultRowIcon ?? 'diamond';

      seriesItems.push({
        key: seriesKey,
        name: displayMeta.name,
        value: yValue,
        color: displayMeta.color,
        dataKey: displayMeta.dataKey,
        dataPoint: datum,
        rowIcon,
        yAxis: displayMeta.yAxis ?? 'left',
      });
    }
  }

  return seriesItems;
}
